import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogClose
} from '@/components/ui/dialog';

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: string[];
  currentIndex: number;
  onPrevious: () => void;
  onNext: () => void;
  alt: string;
}

/**
 * A modal component for displaying enlarged product images
 */
const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  images,
  currentIndex,
  onPrevious,
  onNext,
  alt
}) => {
  if (images.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl p-0 bg-transparent border-0 overflow-hidden" preventFocusManagement>
        <div className="relative flex items-center justify-center w-full h-full">
          {/* Main image */}
          <div className="w-full h-full flex items-center justify-center">
            <img
              src={images[currentIndex]}
              alt={`${alt} - Enlarged Image ${currentIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain"
            />
          </div>

          {/* Navigation buttons */}
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-4 z-50 bg-black/50 hover:bg-black/70 rounded-full p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onPrevious();
                }}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-8 w-8 text-white" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-4 z-50 bg-black/50 hover:bg-black/70 rounded-full p-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onNext();
                }}
                aria-label="Next image"
              >
                <ChevronRight className="h-8 w-8 text-white" />
              </Button>
            </>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white text-sm px-3 py-1 rounded-full">
              {currentIndex + 1} / {images.length}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageModal;